import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

// API Base URL (이미지 경로용)
const API_BASE_URL = 'https://www.hb-volleyball.co.kr/api';

const UniformContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const UniformCard = styled.div<{ hasUniform?: boolean }>`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const UniformStatus = styled.div<{ hasUniform?: boolean }>`
  text-align: center;
  padding: 2rem;
  background: ${props => props.hasUniform ? '#d1e7dd' : '#fff3cd'};
  border: 2px solid ${props => props.hasUniform ? '#badbcc' : '#ffc107'};
  border-radius: 12px;
  margin-bottom: 2rem;
`;

const StatusIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const StatusTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const StatusMessage = styled.div`
  font-size: 1rem;
  color: #6c757d;
  line-height: 1.6;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InfoItem = styled.div`
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const InfoValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ActionSection = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const InfoBox = styled.div`
  background: #e7f5ff;
  border: 1px solid #339af0;
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.875rem;
  color: #1971c2;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ImageModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ImageModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImageModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
`;

const ImageModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const ImageModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ImageContainer = styled.div`
  max-width: 100%;
  max-height: calc(90vh - 80px);
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 80px);
  object-fit: contain;
  border-radius: 8px;
`;

const ImageButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const ImageButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  &:active {
    background: #dee2e6;
  }
`;

interface Uniform {
  version: number;
  number: string;
  nickname: string;
  size: string;
}

const MemberUniform: React.FC = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    number: '',
    nickname: '',
    size: '',
    upperSize: '',
    lowerSize: ''
  });
  const [myUniforms, setMyUniforms] = useState<Uniform[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);  // 수정 모드 여부
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [currentImageTitle, setCurrentImageTitle] = useState<string>('');
  const [availableNumbers, setAvailableNumbers] = useState<string[]>([]);
  const [loadingAvailableNumbers, setLoadingAvailableNumbers] = useState(false);
  const [numberError, setNumberError] = useState('');
  
  // 내 유니폼 정보 조회 (구 유니폼 제외)
  const fetchMyUniforms = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/uniforms/my-uniform');
      if (response.data.success) {
        // version 1 (구 유니폼) 제외
        const filteredUniforms = response.data.data.filter((uniform: Uniform) => uniform.version !== 1);
        setMyUniforms(filteredUniforms);
      }
    } catch (error) {
      console.error('유니폼 정보 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyUniforms();
  }, []);

  // 첫 번째 유니폼 정보 (기본 표시용)
  const myUniform = myUniforms.length > 0 ? myUniforms[0] : null;

  const [selectedVersion, setSelectedVersion] = useState<number>(1);

  // 사용 가능한 배번 목록 조회
  const fetchAvailableNumbers = async (version: number = 2) => {
    try {
      setLoadingAvailableNumbers(true);
      const response = await apiClient.get(`/uniforms/available-numbers?version=${version}`);
      if (response.data.success) {
        setAvailableNumbers(response.data.data.available_numbers || []);
      }
    } catch (error) {
      console.error('사용 가능한 배번 목록 조회 실패:', error);
      setAvailableNumbers([]);
    } finally {
      setLoadingAvailableNumbers(false);
    }
  };

  // 배번 사용 가능 여부 확인
  const checkNumberAvailable = async (number: string, version: number = 2) => {
    if (!number.trim()) {
      setNumberError('');
      return true;
    }

    try {
      const response = await apiClient.get(`/uniforms/check-number/${number}?version=${version}`);
      if (response.data.success) {
        const isAvailable = response.data.data.is_available;
        if (!isAvailable) {
          setNumberError('이미 사용 중인 배번이거나 배번 규칙에 위배됩니다.');
          return false;
        } else {
          setNumberError('');
          return true;
        }
      }
      return false;
    } catch (error: any) {
      console.error('배번 확인 실패:', error);
      const errorMessage = error.response?.data?.detail || '배번 확인 중 오류가 발생했습니다';
      setNumberError(errorMessage);
      return false;
    }
  };

  const handleApplyClick = async (uniform?: Uniform) => {
    if (uniform) {
      // 기존 유니폼 수정
      let upperSize = '';
      let lowerSize = '';
      
      // 기존 사이즈 파싱 ("상의 {upperSize}, 하의 {lowerSize}" 형식)
      if (uniform.size) {
        const sizeMatch = uniform.size.match(/상의\s+([^,]+),\s*하의\s+(.+)/);
        if (sizeMatch) {
          upperSize = sizeMatch[1].trim();
          lowerSize = sizeMatch[2].trim();
        } else {
          // 기존 형식이 아니면 전체를 상의로 처리
          upperSize = uniform.size;
          lowerSize = '';
        }
      }
      
      setFormData({
        number: uniform.number || '',
        nickname: uniform.nickname || '',
        size: uniform.size || '',
        upperSize: upperSize,
        lowerSize: lowerSize
      });
      setSelectedVersion(uniform.version);
      setIsEditing(true);
      setNumberError('');
      setAvailableNumbers([]);
    } else {
      // 새 유니폼 신청 (버전 1 또는 2 중 선택)
      setFormData({
        number: '',
        nickname: '',
        size: '',
        upperSize: '',
        lowerSize: ''
      });
      // 기존 유니폼이 있으면 다음 버전, 없으면 버전 2 (구 유니폼은 버전 1)
      const maxVersion = myUniforms.length > 0 
        ? Math.max(...myUniforms.map(u => u.version))
        : 1;
      const nextVersion = maxVersion + 1;
      setSelectedVersion(nextVersion >= 2 ? nextVersion : 2);
      setIsEditing(false);
      setNumberError('');
      // 사용 가능한 배번 목록 조회
      await fetchAvailableNumbers(nextVersion >= 2 ? nextVersion : 2);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.user_no) {
      alert('로그인이 필요합니다.');
      return;
    }

    // 사이즈 유효성 검사
    if (!formData.upperSize.trim() || !formData.lowerSize.trim()) {
      alert('상의 사이즈와 하의 사이즈를 모두 입력해주세요.');
      return;
    }

    // 신청 모드일 때 배번 필수 체크
    if (!isEditing && !formData.number.trim()) {
      alert('배번을 입력해주세요.');
      return;
    }

    // 신청 모드일 때 배번 유효성 검사
    if (!isEditing && formData.number.trim()) {
      const isAvailable = await checkNumberAvailable(formData.number.trim(), selectedVersion);
      if (!isAvailable) {
        return; // 에러 메시지는 checkNumberAvailable에서 설정됨
      }
    }

    try {
      // 사이즈를 "상의 {upperSize}, 하의 {lowerSize}" 형식으로 합치기
      const sizeString = `상의 ${formData.upperSize.trim()}, 하의 ${formData.lowerSize.trim()}`;
      
      // 선택된 버전으로 저장
      // 수정 모드일 때는 배번 제외 (배번은 수정 불가)
      const updateData: any = {
        size: sizeString
      };
      
      // nickname이 있으면 포함
      if (formData.nickname.trim()) {
        updateData.nickname = formData.nickname.trim();
      }
      
      // 신청 모드일 때만 배번 포함
      if (!isEditing) {
        if (formData.number.trim()) {
          updateData.number = formData.number.trim();
        }
      }
      
      const response = await apiClient.put(`/uniforms/${user.user_no}/${selectedVersion}`, updateData);
      
      if (response.data.success) {
        window.alert('유니폼 정보가 저장되었습니다!');
        fetchMyUniforms(); // 목록 새로고침
        setIsModalOpen(false);
        setIsEditing(false);
        setNumberError('');
        setAvailableNumbers([]);
      }
    } catch (error: any) {
      console.error('유니폼 저장 실패:', error);
      console.error('에러 응답:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || '유니폼 저장에 실패했습니다';
      setNumberError(errorMessage);
      window.alert(`유니폼 저장 실패: ${errorMessage}`);
    }
  };

  return (
    <DashboardLayout title="유니폼">
      <UniformContainer>
        <Title>내 유니폼</Title>
        
        {loading ? (
          <UniformCard>
            <div style={{ textAlign: 'center', padding: '2rem' }}>로딩 중...</div>
          </UniformCard>
        ) : (
          <>
            <UniformStatus hasUniform={myUniforms.length > 0}>
              <StatusIcon>{myUniforms.length > 0 ? '👕' : '❗'}</StatusIcon>
              <StatusTitle>
                {myUniforms.length > 0 ? '유니폼 등록 완료' : '유니폼 미등록'}
              </StatusTitle>
              <StatusMessage>
                {myUniforms.length > 0 
                  ? `${myUniforms.length}개의 유니폼이 등록되어 있습니다.`
                  : '유니폼을 신청해주세요.'}
              </StatusMessage>
            </UniformStatus>
            
            {/* 디자인 및 사이즈 표 버튼 */}
            <UniformCard>
              <div style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 700, color: '#000000' }}>
                디자인 및 사이즈 표
              </div>
              <ImageButtonGroup>
                <ImageButton
                  onClick={() => {
                    const imageUrl = `${API_BASE_URL}/data/design.png`;
                    setCurrentImageUrl(imageUrl);
                    setCurrentImageTitle('디자인');
                    setIsImageModalOpen(true);
                  }}
                >
                  🎨 디자인보기
                </ImageButton>
                <ImageButton
                  onClick={() => {
                    const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size upper.jpeg')}`;
                    setCurrentImageUrl(imageUrl);
                    setCurrentImageTitle('상의 사이즈 표 (여자)');
                    setIsImageModalOpen(true);
                  }}
                >
                  📏 상의 사이즈 표 (여자)
                </ImageButton>
                <ImageButton
                  onClick={() => {
                    const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size upper m.jpeg')}`;
                    setCurrentImageUrl(imageUrl);
                    setCurrentImageTitle('상의 사이즈 표 (공용)');
                    setIsImageModalOpen(true);
                  }}
                >
                  📏 상의 사이즈 표 (공용)
                </ImageButton>
                <ImageButton
                  onClick={() => {
                    const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size lower.jpeg')}`;
                    setCurrentImageUrl(imageUrl);
                    setCurrentImageTitle('하의 사이즈 표 (여자)');
                    setIsImageModalOpen(true);
                  }}
                >
                  📏 하의 사이즈 표 (여자)
                </ImageButton>
                <ImageButton
                  onClick={() => {
                    const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size lower m.jpeg')}`;
                    setCurrentImageUrl(imageUrl);
                    setCurrentImageTitle('하의 사이즈 표 (공용)');
                    setIsImageModalOpen(true);
                  }}
                >
                  📏 하의 사이즈 표 (공용)
                </ImageButton>
              </ImageButtonGroup>
            </UniformCard>
            
            {myUniforms.length > 0 && myUniforms.map((uniform, index) => (
              <UniformCard key={uniform.version} hasUniform>
                <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6c757d', fontWeight: 600 }}>
                  {uniform.version === 1 ? '구 유니폼' : uniform.version === 2 ? 'new' : `유니폼 v${uniform.version}`}
                </div>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>배번</InfoLabel>
                    <InfoValue>{uniform.number || '미등록'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>닉네임</InfoLabel>
                    <InfoValue>{uniform.nickname || '미등록'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>사이즈</InfoLabel>
                    <InfoValue style={{ fontSize: '1rem', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                      {uniform.size || '미등록'}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>이름</InfoLabel>
                    <InfoValue>{user?.name}</InfoValue>
                  </InfoItem>
                </InfoGrid>
                <ActionSection>
                  <Button 
                    variant="outline" 
                    size="medium"
                    onClick={() => handleApplyClick(uniform)}
                  >
                     {uniform.version === 1 ? '구 유니폼' : uniform.version === 2 ? '신규 유니폼' : `v${uniform.version}`} 정보 수정
                  </Button>
                </ActionSection>
              </UniformCard>
            ))}
            
            {myUniforms.length === 0 && (
              <UniformCard>
                <ActionSection>
                  <Button 
                    variant="primary" 
                    size="large"
                    onClick={() => handleApplyClick()}
                  >
                    유니폼 신청하기
                  </Button>
                </ActionSection>
              </UniformCard>
            )}
          </>
        )}
        
        {/* 유니폼 신청/수정 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => {
            setIsModalOpen(false);
            setIsEditing(false);
            setNumberError('');
            setAvailableNumbers([]);
            setFormData({
              number: '',
              nickname: '',
              size: '',
              upperSize: '',
              lowerSize: ''
            });
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedVersion === 1 ? '구 유니폼' : selectedVersion === 2 ? 'new' : `유니폼 v${selectedVersion}`} {myUniforms.some(u => u.version === selectedVersion) ? '수정' : '신청'}
              </ModalTitle>
              <CloseButton onClick={() => {
                setIsModalOpen(false);
                setIsEditing(false);
                setNumberError('');
                setAvailableNumbers([]);
                setFormData({
                  number: '',
                  nickname: '',
                  size: '',
                  upperSize: '',
                  lowerSize: ''
                });
              }}>×</CloseButton>
            </ModalHeader>
            
            <InfoBox>
              <strong>배번 중복 규칙:</strong>
              <br />
              • 같은 성별은 배번 중복 불가
              <br />
              • 다른 성별은 중복 가능
              <br />
              • 단, 선출끼리는 성별 관계없이 중복 불가
            </InfoBox>
            
            <ModalForm onSubmit={handleSubmit}>
              {!isEditing && (
                <>
                  {/* 사용 가능한 배번 목록 표시 */}
                  {loadingAvailableNumbers ? (
                    <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                      사용 가능한 배번을 불러오는 중...
                    </p>
                  ) : availableNumbers.length > 0 ? (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#6c757d', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                        선택 가능한 배번 ({availableNumbers.length}개):
                      </p>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginBottom: '1rem'
                      }}>
                        {availableNumbers.map((num) => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, number: num });
                              setNumberError('');
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: formData.number === num ? '#000000' : '#ffffff',
                              color: formData.number === num ? '#ffffff' : '#000000',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: formData.number === num ? 600 : 400,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (formData.number !== num) {
                                e.currentTarget.style.background = '#e9ecef';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (formData.number !== num) {
                                e.currentTarget.style.background = '#ffffff';
                              }
                            }}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p style={{ fontSize: '0.875rem', color: '#dc3545', marginBottom: '1rem' }}>
                      사용 가능한 배번이 없습니다.
                    </p>
                  )}
                </>
              )}
              
              <Input
                label="배번"
                type="text"
                value={formData.number}
                onChange={(e) => {
                  const value = e.target.value;
                  // 숫자만 입력 가능 (1-99 범위)
                  if (value === '' || /^\d+$/.test(value)) {
                    const num = value === '' ? 0 : parseInt(value);
                    if (value === '' || (num >= 1 && num <= 99)) {
                      setFormData({ ...formData, number: value });
                      setNumberError('');
                    }
                  }
                }}
                disabled={isEditing}
                placeholder="예: 10 (1-99번)"
                required={!isEditing}
              />
              {isEditing && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
                  배번은 수정할 수 없습니다.
                </div>
              )}
              {!isEditing && numberError && (
                <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
                  {numberError}
                </div>
              )}
              
              <Input
                label="닉네임"
                type="text"
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                placeholder="예: 철수"
              />
              
              <Input
                label="상의 사이즈 (여자 사이즈일 경우 w를 꼭 기입해 주세요.)"
                type="text"
                value={formData.upperSize}
                onChange={(e) => setFormData({ ...formData, upperSize: e.target.value })}
                placeholder="예: S, M, L, XL 또는 w M, w L"
              />
              
              <Input
                label="하의 사이즈 (여자 사이즈일 경우 w를 꼭 기입해 주세요.)"
                type="text"
                value={formData.lowerSize}
                onChange={(e) => setFormData({ ...formData, lowerSize: e.target.value })}
                placeholder="예: S, M, L, XL 또는 w M, w L"
              />
              
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                <Button
                  variant="outline"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsEditing(false);
                    setNumberError('');
                    setAvailableNumbers([]);
                    setFormData({
                      number: '',
                      nickname: '',
                      size: '',
                      upperSize: '',
                      lowerSize: ''
                    });
                  }}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  {isEditing ? '수정' : '신청'}
                </Button>
              </div>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
        
        {/* 이미지 모달 */}
        <ImageModalOverlay 
          isOpen={isImageModalOpen}
          onClick={() => setIsImageModalOpen(false)}
        >
          <ImageModalContent onClick={(e) => e.stopPropagation()}>
            <ImageModalHeader>
              <ImageModalTitle>{currentImageTitle}</ImageModalTitle>
              <ImageModalCloseButton onClick={() => setIsImageModalOpen(false)}>
                ×
              </ImageModalCloseButton>
            </ImageModalHeader>
            <ImageContainer>
              <ModalImage 
                src={currentImageUrl} 
                alt={currentImageTitle}
                onError={(e) => {
                  console.error('이미지 로드 실패:', currentImageUrl);
                  e.currentTarget.src = ''; // 에러 이미지 제거
                }}
              />
            </ImageContainer>
          </ImageModalContent>
        </ImageModalOverlay>
      </UniformContainer>
    </DashboardLayout>
  );
};

export default MemberUniform;

